import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ResponsiveLogo } from './Logo';

export interface NavigationItem {
  name: string;
  href: string;
  description?: string;
  fallbackHref?: string;
}

interface UnifiedNavigationProps {
  /** 当前页面标识，用于高亮当前导航项 */
  currentPage?: 'home' | 'analyses' | 'learning' | 'models' | 'features' | 'about';
  /** 是否显示粘性效果（滚动时的背景变化） */
  sticky?: boolean;
  /** 是否已登录 */
  isLoggedIn?: boolean;
  /** 是否为管理员用户 */
  isAdminUser?: boolean;
  /** 登录按钮点击回调 */
  onLoginClick?: () => void;
  /** 注册按钮点击回调 */
  onSignUpClick?: () => void;
  /** 自定义导航项，如果不提供则使用默认项 */
  customNavigationItems?: NavigationItem[];
  /** 额外的CSS类名 */
  className?: string;
}

const UnifiedNavigation: React.FC<UnifiedNavigationProps> = ({
  currentPage,
  sticky = true,
  isLoggedIn,
  isAdminUser,
  onLoginClick,
  onSignUpClick,
  customNavigationItems,
  className = '',
}) => {
  const { user, signOut, isAdminUser: authIsAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // 确定用户登录状态
  const userLoggedIn = isLoggedIn ?? !!user;
  const userIsAdmin = isAdminUser ?? authIsAdmin;

  // 默认导航项
  const defaultNavigationItems: NavigationItem[] = [
    { name: '首页', href: '/', description: '返回首页' },
    { name: '案例精选', href: '/analyses', description: '精选案例分析' },
    { name: '思维训练', href: '/#thinking-training', description: '学习思维模型', fallbackHref: '/learning/articles' },
    { name: '模型工厂', href: '/models', description: '学习思维模型' },
    { name: '功能介绍', href: '/#features', description: '了解产品功能' }
  ];

  const navigationItems = customNavigationItems || defaultNavigationItems;

  // 监听滚动事件，实现粘性导航效果
  useEffect(() => {
    if (!sticky) return;
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sticky]);

  // 平滑滚动到指定元素，支持fallback跳转
  const scrollToSection = (sectionId: string, fallbackHref?: string) => {
    const element = document.getElementById(sectionId);
    if (element && location.pathname === '/') {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (fallbackHref) {
      // 如果找不到元素且有fallback链接，则跳转到fallback页面
      navigate(fallbackHref);
    } else {
      // 跳转到首页并滚动到对应区域
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  // 处理退出登录
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('退出登录失败:', error);
      // 即使出错也导航到首页，因为用户状态会通过AuthContext自动更新
      navigate('/');
    }
  };

  // 判断当前链接是否激活
  const isActiveLink = (href: string) => {
    if (href.startsWith('/#')) {
      return location.pathname === '/' && currentPage === href.substring(2).replace('-', '_');
    }
    // 根路径需要精确匹配，避免匹配到其他以/开头的路径
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header
      className={`${sticky ? 'sticky' : 'relative'} top-0 left-0 right-0 z-50 transition-all duration-300 ${
        sticky && isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/30'
          : 'bg-white/80 backdrop-blur-sm'
      } ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group touch-target min-w-0">
            <ResponsiveLogo 
              size="xl" 
              className="group-hover:scale-105 transition-transform duration-300 flex-shrink-0" 
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <div key={item.name} className="relative group">
                {item.href.startsWith('#') || item.href.startsWith('/#') ? (
                  <button
                    onClick={() => scrollToSection(
                      item.href.startsWith('/#') ? item.href.substring(2) : item.href.substring(1), 
                      item.fallbackHref
                    )}
                    className={`px-4 py-2 font-medium transition-colors duration-200 rounded-lg ${
                      isActiveLink(item.href)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-4 py-2 font-medium transition-colors duration-200 rounded-lg flex items-center group ${
                      isActiveLink(item.href)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    {item.name}
                    {item.description && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                        {item.description}
                        <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                      </div>
                    )}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {!userLoggedIn ? (
              <>
                {onLoginClick && (
                  <button
                    onClick={onLoginClick}
                    className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 font-medium transition-colors duration-200 rounded-lg"
                  >
                    登录
                  </button>
                )}
                {onSignUpClick && (
                  <button
                    onClick={onSignUpClick}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                  >
                    免费注册
                  </button>
                )}
                {!onLoginClick && !onSignUpClick && (
                  <Link
                    to="/"
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    登录/注册
                  </Link>
                )}
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                {userIsAdmin && (
                  <Link
                    to="/dashboard/models"
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                  >
                    继续学习
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  退出
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-3 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-gray-100 transition-colors duration-200 touch-target"
            aria-label="切换菜单"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg safe-area-left safe-area-right">
          <div className="px-4 py-6 space-y-4">
            {/* Mobile Navigation Items */}
            {navigationItems.map((item) => (
              <div key={item.name}>
                {item.href.startsWith('#') || item.href.startsWith('/#') ? (
                  <button
                    onClick={() => {
                      scrollToSection(
                        item.href.startsWith('/#') ? item.href.substring(2) : item.href.substring(1), 
                        item.fallbackHref
                      );
                      setIsMobileMenuOpen(false);
                    }}
                    className={`block text-gray-700 hover:text-blue-600 font-medium py-3 transition-colors duration-200 touch-target w-full text-left ${
                      isActiveLink(item.href) ? 'text-blue-600' : ''
                    }`}
                  >
                    <div className="flex flex-col">
                      <span>{item.name}</span>
                      {item.description && (
                        <span className="text-sm text-gray-500 mt-1">{item.description}</span>
                      )}
                    </div>
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block text-gray-700 hover:text-blue-600 font-medium py-3 transition-colors duration-200 touch-target ${
                      isActiveLink(item.href) ? 'text-blue-600' : ''
                    }`}
                  >
                    <div className="flex flex-col">
                      <span>{item.name}</span>
                      {item.description && (
                        <span className="text-sm text-gray-500 mt-1">{item.description}</span>
                      )}
                    </div>
                  </Link>
                )}
              </div>
            ))}

            {/* Mobile Auth Buttons */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              {!userLoggedIn ? (
                <>
                  {onLoginClick && (
                    <button
                      onClick={() => {
                        onLoginClick();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-center text-gray-700 hover:text-blue-600 font-medium mobile-button border border-gray-300 rounded-lg transition-colors duration-200 button-press"
                    >
                      登录
                    </button>
                  )}
                  {onSignUpClick && (
                    <button
                      onClick={() => {
                        onSignUpClick();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white mobile-button rounded-lg font-medium hover:shadow-lg transition-all duration-200 button-press"
                    >
                      免费注册
                    </button>
                  )}
                  {!onLoginClick && !onSignUpClick && (
                    <Link
                      to="/"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full text-center text-blue-600 hover:bg-blue-50 font-medium mobile-button border border-blue-300 rounded-lg transition-colors duration-200 button-press"
                    >
                      登录/注册
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {userIsAdmin && (
                    <Link
                      to="/dashboard/models"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block w-full text-center bg-gradient-to-r from-blue-600 to-purple-600 text-white mobile-button rounded-lg font-medium hover:shadow-lg transition-all duration-200 button-press"
                    >
                      继续学习
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleSignOut();
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center px-3 py-2 text-base font-medium text-red-600 hover:bg-red-50 rounded-lg w-full"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    退出登录
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default UnifiedNavigation;